import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES, PHONE_DISPLAY, PHONE_TEL, SERVED_AREAS } from "@/constants/site";

const SERVICES = [
  {
    id: "rooftop",
    title: "On-Grid Rooftop Solar",
    tagline: "3 kW – 10 kW+ · Homes, shops & offices",
    desc: "Grid-connected solar that slashes your monthly bill. We size the system to your consumption, install Tier-1 panels with a 25+ year performance warranty, and take care of PM Surya Ghar subsidy filing and MSEDCL net-metering from start to finish.",
    points: ["Free site survey & shadow analysis", "Subsidy up to ₹78,000 handled for you", "Net-metering & DISCOM liaison included", "25–30 year panel performance warranty"],
    image: IMAGES.panels,
  },
  {
    id: "commercial",
    title: "Commercial & Industrial Solar",
    tagline: "10 kW – 100 kW+ · Factories, schools, hospitals",
    desc: "High-capacity plants engineered for heavy day-time loads. Ideal for factories, godowns, schools, hotels and hospitals where daytime consumption is at its peak — exactly when solar output is highest.",
    points: ["Custom structural & load design", "Accelerated payback on heavy tariffs", "Net-metering / open-access guidance", "AMC options for long-term performance"],
    image: IMAGES.sunset,
  },
  {
    id: "offgrid",
    title: "Off-Grid & Hybrid Systems",
    tagline: "Battery-backed power for irregular supply areas",
    desc: "Systems with lithium battery backup that keep your home or business running through outages — perfect for farms, rural sites and areas with unreliable grid supply.",
    points: ["Lithium & advanced battery options", "Hybrid designs: savings + backup", "Solar water pump integration", "Remote monitoring setup available"],
    image: IMAGES.hero,
  },
  {
    id: "street-lights",
    title: "Solar Street Lights & Control Panels",
    tagline: "Farms, societies & gram panchayats",
    desc: "Supply and installation of solar street lighting for farms, housing societies and panchayats, plus control panels and electrical equipment from our wholesale range — with professional commissioning.",
    points: ["Dusk-to-dawn automatic operation", "Bulk supply at wholesale rates", "Control panels & electricals in-house", "Installation & commissioning included"],
    image: IMAGES.engineer,
  },
];

const COMPARISON = {
  cols: ["On-Grid", "Off-Grid", "Hybrid"],
  rows: [
    ["Best suited for", "Homes & shops with reliable grid", "Farms & areas with frequent cuts", "Savings + backup together"],
    ["Monthly bill savings", "Up to 90%", "Saves diesel / generator cost", "Up to 90% + backup"],
    ["Battery included", "Optional", "Required", "Included"],
    ["PM Surya Ghar subsidy", "Yes", "Limited", "Yes"],
    ["Power during outages", "No", "Yes", "Yes"],
    ["Typical size", "3–10 kW", "1–5 kW", "3–10 kW"],
  ],
};

function ServicesPage() {
  return (
    <div>
      {/* HERO */}
      <section className="bg-gradient-to-b from-amber-50/80 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-600" data-testid="services-eyebrow">
            Our Services
          </p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950 max-w-3xl">
            Everything the sun can do for your roof.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
            From a 3 kW home rooftop to a 100 kW industrial plant — designed, installed
            and maintained by one local team across {SERVED_AREAS}.
          </p>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20 lg:space-y-28">
          {SERVICES.map((s, i) => (
            <div key={s.id} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <Reveal className={i % 2 === 1 ? "order-1 lg:order-2" : ""}>
                <div className="relative">
                  <div className="pointer-events-none absolute -inset-5 rounded-[2.5rem] bg-amber-200/25 blur-2xl" />
                  <div className="relative overflow-hidden rounded-[2rem] ring-1 ring-slate-900/10 shadow-xl">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="h-64 sm:h-80 lg:h-[420px] w-full object-cover"
                      data-testid={`service-image-${s.id}`}
                    />
                  </div>
                </div>
              </Reveal>
              <div className={i % 2 === 1 ? "order-2 lg:order-1" : ""}>
                <Reveal>
                  <p className="font-mono text-xs font-semibold text-orange-600 uppercase tracking-[0.2em]">
                    {String(i + 1).padStart(2, "0")} · {s.tagline}
                  </p>
                  <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex gap-3 text-sm font-medium text-slate-700">
                        <CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to="/contact"
                      data-testid={`service-quote-button-${s.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-6 py-3 text-sm font-semibold text-white hover:bg-orange-700 transition-colors"
                    >
                      Get Free Quote <ArrowRight className="h-4 w-4" />
                    </Link>
                    <a
                      href={PHONE_TEL}
                      data-testid={`service-call-button-${s.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 hover:border-orange-400 hover:text-orange-600 transition-colors"
                    >
                      <Phone className="h-4 w-4 text-orange-600" /> {PHONE_DISPLAY}
                    </a>
                  </div>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-16 lg:py-24 bg-slate-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-500">Compare Systems</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              On-Grid vs Off-Grid vs Hybrid
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 overflow-x-auto rounded-3xl border border-slate-800">
            <table data-testid="comparison-table" className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="bg-slate-900 text-amber-400">
                  <th className="px-6 py-4 font-semibold">Feature</th>
                  {COMPARISON.cols.map((c) => (
                    <th key={c} className="px-6 py-4 font-display font-bold text-white">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {COMPARISON.rows.map((row, ri) => (
                  <tr key={row[0]} className={ri % 2 === 0 ? "bg-slate-900/40" : "bg-slate-900/10"}>
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-6 py-4 ${ci === 0 ? "font-semibold text-white" : ""}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-7xl mx-auto">
          <div className="rounded-[2rem] bg-gradient-to-br from-orange-600 to-amber-600 px-6 py-14 sm:px-12 text-center text-white">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              Not sure which system fits your roof?
            </h2>
            <p className="mt-3 text-white/85 text-base sm:text-lg max-w-xl mx-auto">
              Book a free site survey — we will recommend the right system, not the biggest one.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                data-testid="services-cta-quote-button"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-orange-700 shadow-lg hover:bg-amber-50 transition-colors"
              >
                Book Free Survey <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="services-cta-call-button"
                className="inline-flex items-center gap-2 rounded-full border border-white/50 px-8 py-4 text-base font-semibold text-white hover:bg-white/10 transition-colors"
              >
                <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

export default ServicesPage;
